import axios from "axios";

export default {
  namespaced: true,

  state: {
    activeEvents: {},
    currentCategory: null,
    currentEvents: [],
    claimHistory: [],
    isLoading: false,
    lastFetch: null,
  },

  getters: {
    activeEvents: (state) => state.activeEvents,
    currentEvents: (state) => state.currentEvents,
    claimHistory: (state) => state.claimHistory,
    isLoading: (state) => state.isLoading,
    getEventsByCategory: (state) => (category) => {
      return state.activeEvents[category] || [];
    },
  },

  mutations: {
    SET_ACTIVE_EVENTS(state, events) {
      state.activeEvents = events;
      state.lastFetch = new Date();
    },

    SET_CURRENT_CATEGORY(state, category) {
      state.currentCategory = category;
    },

    SET_CURRENT_EVENTS(state, events) {
      state.currentEvents = events;
    },

    SET_CLAIM_HISTORY(state, history) {
      state.claimHistory = history;
    },

    SET_LOADING(state, loading) {
      state.isLoading = loading;
    },

    UPDATE_TIER_STATUS(state, { eventId, tierId, status }) {
      // Find and update the tier status in current events
      const event = state.currentEvents.find((e) => e.id === eventId);
      if (event && event.user_status && event.user_status.tiers) {
        const tier = event.user_status.tiers.find((t) => t.tier_id === tierId);
        if (tier) {
          tier.status = status;
          tier.is_claimed = status === "claimed";
        }
      }
    },

    CLEAR_STATE(state) {
      state.activeEvents = {};
      state.currentCategory = null;
      state.currentEvents = [];
      state.claimHistory = [];
      state.isLoading = false;
      state.lastFetch = null;
    },
  },

  actions: {
    /**
     * Fetch all active events grouped by category.
     */
    async fetchActiveEvents({ commit }) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.get("/rewards/active");
        commit("SET_ACTIVE_EVENTS", response.data.data);
        return response.data.data;
      } catch (error) {
        console.error("Error fetching active events:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    /**
     * Fetch events for a specific category with user status.
     */
    async fetchEventsByCategory({ commit }, category) {
      commit("SET_LOADING", true);
      commit("SET_CURRENT_CATEGORY", category);
      try {
        const response = await axios.get(`/rewards/category/${category}`);
        commit("SET_CURRENT_EVENTS", response.data.data);
        return response.data.data;
      } catch (error) {
        console.error("Error fetching events by category:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    /**
     * Fetch event details by slug.
     */
    async fetchEventBySlug({ commit }, slug) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.get(`/rewards/event/${slug}`);
        return response.data.data;
      } catch (error) {
        console.error("Error fetching event:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    /**
     * Claim a reward tier.
     */
    async claimReward({ commit, dispatch, state }, tierId) {
      try {
        const response = await axios.post("/rewards/claim", { tier_id: tierId });

        if (response.data.success) {
          // Update the tier status locally
          const currentEvent = state.currentEvents.find((e) =>
            e.user_status?.tiers?.some((t) => t.tier_id === tierId)
          );

          if (currentEvent) {
            commit("UPDATE_TIER_STATUS", {
              eventId: currentEvent.id,
              tierId,
              status: "claimed",
            });
          }

          // Refresh current category to get updated data
          if (state.currentCategory) {
            dispatch("fetchEventsByCategory", state.currentCategory);
          }
        }

        return response.data;
      } catch (error) {
        console.error("Error claiming reward:", error);
        throw error;
      }
    },

    /**
     * Fetch user's claim history.
     */
    async fetchClaimHistory({ commit }, params = {}) {
      commit("SET_LOADING", true);
      try {
        const response = await axios.get("/rewards/history", { params });
        commit("SET_CLAIM_HISTORY", response.data.data);
        return response.data;
      } catch (error) {
        console.error("Error fetching claim history:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    /**
     * Fetch ranking for an event.
     */
    async fetchRanking(_, { slug, limit = 20 }) {
      try {
        const response = await axios.get(`/rewards/ranking/${slug}`, {
          params: { limit },
        });
        return response.data.data;
      } catch (error) {
        console.error("Error fetching ranking:", error);
        throw error;
      }
    },

    /**
     * Fetch event gifts for user display.
     */
    async fetchEventGifts(_, slug) {
      try {
        const response = await axios.get(`/rewards/event/${slug}/gifts`);
        return response.data.data;
      } catch (error) {
        console.error("Error fetching event gifts:", error);
        throw error;
      }
    },

    /**
     * Fetch event with full details including gifts and ranking (for Weekly Star page).
     */
    async fetchEventWithRanking({ commit }, slug) {
      commit("SET_LOADING", true);
      try {
        const [eventResponse, giftsResponse] = await Promise.all([
          axios.get(`/rewards/event/${slug}`),
          axios.get(`/rewards/event/${slug}/gifts`).catch(() => ({ data: { data: [] } })),
        ]);

        const eventData = eventResponse.data.data;
        eventData.event_gifts = giftsResponse.data.data || [];

        return eventData;
      } catch (error) {
        console.error("Error fetching event with ranking:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    /**
     * Clear rewards state (e.g., on logout).
     */
    clearState({ commit }) {
      commit("CLEAR_STATE");
    },
  },
};
